/**
 * @typedef {Object} CustomerBuysCollection
 * @property {string} handle
 * @property {string} id
 * @property {number} productCount
 * @property {string} title
 */

/**
 * @typedef {Object} CustomerBuysProductVariant
 * @property {string} id
 * @property {string} price
 */

/**
 * @typedef {Object} CustomerBuysProduct
 * @property {string} handle
 * @property {string} id
 * @property {string} title
 * @property {CustomerBuysProductVariant[]} variants
 * @property {number} variantCount
 */

/**
 * @typedef {Object} CustomerBuysConfig
 * @property {string} amount
 * @property {CustomerBuysCollection[]} collections
 * @property {'MinimumPurchase' | 'MinimumQuantity'} discountType
 * @property {'Collection' | 'Product'} itemType
 * @property {CustomerBuysProduct[]} products
 * @property {string} quantity
 * @property {string} query
 */

/**
 * @typedef {Object} GwpCampaign
 * @property {boolean} applyCodeAutomatically
 * @property {string} discountCode
 * @property {string} campaignId
 * @property {boolean} passedThreshold
 * @property {number|null} autoAddVariantId
 * @property {number} autoAddQuantity
 */

const { fetchJSON, getDefaultRequestConfig } = window.BAO.Utils

class GiftWithPurchase {
  constructor () {
    this.onPushCartChange = this.onPushCartChange.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_RENDER,
      this.onPushCartChange,
    )

    this.init()
  }

  /**
   * Removes orphaned GWP items on page load.
   */
  async init () {
    try {
      this.state = await fetchJSON('/cart.js', {
        ...getDefaultRequestConfig(),
        method: 'GET'
      })
      await this.removeOrphanedGwpItems(this.state)
    } catch (err) {
      console.error('GWP init error:', err)
    }
  }

  /**
   * @param {CustomEvent} event
   */
  async onPushCartChange (event) {
    this.state = event.detail.state ?? await fetchJSON(`/cart.js`, {
      ...getDefaultRequestConfig(),
      method: 'GET'
    })
    
    if (!this.shouldRun(this.state)) return
    
    await this.removeOrphanedGwpItems(this.state)

    const cartIsValid = this.getCartIsValid()

    const alreadyInCart = this.hasItemInCart(this.state, this.data.campaignId)

    if (!cartIsValid) {
      await this.removeGwpItems(this.state)

      this.clearTrackedStateOfAutoAddedGwpItem()
      this.clearTrackedStateOfAutoOpenedGwpModal()
    }

    this.maybeAutoOpenSelectionModal(this.state, cartIsValid)

    if (!this.applyDiscountCodeAutomatically) return

    const alreadyHasDiscountCode = this.getAlreadyHasDiscountCode(this.state)

    if (alreadyHasDiscountCode && !cartIsValid) {
      await this.removeDiscountCode(this.state)
    } else if (cartIsValid && !alreadyHasDiscountCode) {
      await this.addDiscountCode(this.state)
    }

    if (
      this.data.passedThreshold &&
      this.data.autoAddVariantId &&
      !alreadyInCart &&
      !this.hasRemovedAutoAddedGwpItem()
    ) {
      this.trackRemovalOfAutoAddedGwpItem()
      await this.autoAddGwpItem()
    } else {
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.RENDER, {
        state: this.state,
      })
    }
  }

  /**
   * @param {Object} state
   * @param {string} campaignId
   *
   * @returns {boolean}
   */
  hasItemInCart (state, campaignId) {
    return state.items.some(
      (item) =>
        item.properties?._gwp === 'true' &&
        item.properties?._gwp_campaign === campaignId,
    )
  }

  async autoAddGwpItem () {
    await window.BAO.Cart.addItem({
      id: this.data.autoAddVariantId,
      quantity: this.data.autoAddQuantity,
      properties: {
        _gwp: 'true',
        _gwp_campaign: this.data.campaignId,
      },
    })
  }

  trackRemovalOfAutoAddedGwpItem () {
    sessionStorage.setItem(`has_removed_auto_added_gwp_${this.data.campaignId}`, 'true')
  }

  /**
   * @returns {boolean}
   */
  hasRemovedAutoAddedGwpItem () {
    return sessionStorage.getItem(`has_removed_auto_added_gwp_${this.data.campaignId}`) === 'true'
  }

  clearTrackedStateOfAutoAddedGwpItem () {
    sessionStorage.removeItem(`has_removed_auto_added_gwp_${this.data.campaignId}`)
  }

  trackAutoOpenedGwpModal () {
    sessionStorage.setItem(`has_opened_multi_gwp_modal_${this.data.campaignId}`, 'true')
  }

  /**
   * @returns {boolean}
   */
  hasAutoOpenedGwpModal () {
    return sessionStorage.getItem(`has_opened_multi_gwp_modal_${this.data.campaignId}`) === 'true'
  }

  clearTrackedStateOfAutoOpenedGwpModal () {
    sessionStorage.removeItem(`has_opened_multi_gwp_modal_${this.data.campaignId}`)
  }

  /**
   * Auto-opens the selection modal once per threshold-pass cycle when a campaign has
   * more than one selectable GWP product and no campaign gift is in cart yet.
   *
   * @param {Object} state
   * @param {boolean} cartIsValid
   */
  maybeAutoOpenSelectionModal (state, cartIsValid) {
    const hasItemInCampaignCart = this.hasItemInCart(state, this.data.campaignId)
    const hasOpenedThisCycle = this.hasAutoOpenedGwpModal()

    if (!cartIsValid) return
    if (!this.hasMultipleGwpProducts) return
    if (hasItemInCampaignCart) return
    if (hasOpenedThisCycle) return

    this.trackAutoOpenedGwpModal()

    window.BAO.dispatchEvent(window.BAO.EVENTS.GIFT_WITH_PURCHASE.OPEN_MODAL, {
      campaignId: this.data.campaignId,
    })
  }

  /**
   * @param {Object} state
   */
  addDiscountCode (state) {
    return window.BAO.Cart.updateDiscount(
      [
        ...this.getCurrentAppliedDiscountCodes(state).filter(
          (code) => code !== this.discountCode,
        ),
        this.discountCode,
      ].join(','),
    )
  }

  /**
   * @param {Object} state
   */
  removeDiscountCode (state) {
    return window.BAO.Cart.updateDiscount(
      [
        ...this.getCurrentAppliedDiscountCodes(state).filter(
          (code) => code !== this.discountCode,
        ),
      ].join(','),
    )
  }

  /**
   * Removes GWP items belonging to campaigns that are no longer active.
   *
   * @param {Object} state
   */
  async removeOrphanedGwpItems (state) {
    const orphanedItems = state.items.filter(
      (item) =>
        item.properties?._gwp === 'true' && this.data.campaignId !== item.properties?._gwp_campaign,
    )

    for (const item of orphanedItems) {
      await window.BAO.Cart.removeItem(item.key)
    }
  }

  /**
   * Removes all GWP items for a specific campaign.
   *
   * @param {Object} state
   */
  async removeGwpItems (state) {
    const gwpItems = state.items.filter(
      (item) =>
        item.properties?._gwp === 'true'
    )

    if (gwpItems.length === 0) return

    for (const item of gwpItems) {
      await window.BAO.Cart.removeItem(item.key)
    }
  }

  /**
   * @param {Object} state
   *
   * @returns {boolean}
   */
  getAlreadyHasDiscountCode (state) {
    return state.discount_codes.some((discountCode) => discountCode.code === this.discountCode)
  }

  /**
   * @param {Object} state
   *
   * @returns {string[]}
   */
  getCurrentAppliedDiscountCodes (state) {
    return state.discount_codes.map((discountCode) => discountCode.code)
  }

  /**
   * @returns {boolean}
   */
  getCartIsValid () {
    return this.data.passedThreshold
  }

  shouldRun (state) {
    if (!state) {
      return !!document.querySelector('script[data-gift-with-purchase-data]')
    }

    const sections = state.sections

    if (!sections || !sections['push-cart-items']) return false

    const section =  new DOMParser().parseFromString(sections['push-cart-items'], 'text/html')
    return !!section.querySelector('script[data-gift-with-purchase-data]')
  }

  get applyDiscountCodeAutomatically () {
    if (!this.shouldRun(this.state)) return false

    return this.data.applyCodeAutomatically
  }

  get discountCode () {
    if (!this.shouldRun(this.state)) return null

    return this.data.discountCode
  }

  /**
   * @returns {HTMLScriptElement|null}
   */
  get dataElement () {
    if (!this.state) {
      return document.querySelector('script[data-gift-with-purchase-data]')
    }

    const sections = this.state.sections

    if (!sections || !sections['push-cart-items']) return null

    const section =  new DOMParser().parseFromString(sections['push-cart-items'], 'text/html')
    return section.querySelector('script[data-gift-with-purchase-data]')
  }

  /**
   * @returns {GwpCampaign}
   */
  get data () {
    return JSON.parse(this.dataElement.textContent.trim())
  }

  get hasMultipleGwpProducts () {
    if (!this.shouldRun(this.state)) return false

    return Number(this.data.productCount) > 1
  }
}

window.BAO.GiftWithPurchase = new GiftWithPurchase()

class GiftWithPurchaseItem extends window.BAO.CustomElement() {
  static requiredAttributes () {
    return ['data-product-handle']
  }

  static requiredElements () {
    return ['form', 'idInput', 'product-option']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'change',
      this.onFormChange.bind(this),
    )
  }

  async onFormChange (e) {
    const chosenOption = Array.from(e.target.options).find(
      (option) => option.value === e.target.value,
    )
    if (!chosenOption) {
      console.error(`Unable to find option for ${e.target.value}`)

      return
    }

    this.setIdInputValue(chosenOption.getAttribute('data-variant-id'))

    if (this.allValuesAreFilled) {
      return this.els.form.element.requestSubmit()
    }

    const productOptionValues = this.els['product-option'].elements.map(
      (option) => {
        if (option.value === '') {
          return option.getAttribute('data-default-option-value-id')
        }

        const selectedOption = Array.from(option.options).find(
          (o) => o.value === option.value,
        )

        return selectedOption.getAttribute('data-option-id')
      },
    )

    const response = await window.BAO.sectionRenderer.fetchSingle(
      'product-gift-with-purchase-unclaimed',
      `/products/${this.getAttribute('data-product-handle')}?option_values=${productOptionValues.join(',')}`,
    )
    const newSection = new DOMParser().parseFromString(response, 'text/html')

    const newProductOptions = Array.from(
      newSection.querySelectorAll(this.els['product-option'].selector),
    )

    for (const newProductOption of newProductOptions) {
      const currentProductOption = this.els['product-option'].elements.find(
        (option) =>
          option.getAttribute('name') === newProductOption.getAttribute('name'),
      )
      if (!currentProductOption) continue

      for (let i = 0; i < currentProductOption.options.length; i++) {
        const currentOption = currentProductOption.options[i]
        const newOption = newProductOption.options[i]

        if (currentOption.value === '') continue

        currentOption.setAttribute(
          'data-variant-id',
          newOption.getAttribute('data-variant-id'),
        )

        if (!currentOption.hasAttribute('data-perma-disabled')) {
          currentOption.disabled = newOption.disabled
        }
      }
    }
  }

  /**
   * @param {string} value
   */
  setIdInputValue (value) {
    this.els.idInput.element.value = value
  }

  get allValuesAreFilled () {
    return this.els['product-option'].elements.every((option) => {
      return option.value !== ''
    })
  }
}

if (!customElements.get('gift-with-purchase-item')) {
  customElements.define('gift-with-purchase-item', GiftWithPurchaseItem)
}

class GiftWithPurchasePDP extends HTMLElement {
  connectedCallback () {
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartChange.bind(this),
    )
  }

  async onCartChange () {
    const sectionId = this.closest("[data-section-type='product-details']")?.dataset.sectionId
    if (!sectionId) return

    const response = await window.BAO.sectionRenderer.fetchSingle(
      sectionId,
      window.location.pathname,
    )

    const newDOM = window.BAO.sectionRenderer.convertToDOM(response)
    const newContent = newDOM.querySelector('gift-with-purchase-pdp')

    if (newContent) {
      this.innerHTML = newContent.innerHTML
    }
  }
}

if (!customElements.get('gift-with-purchase-pdp')) {
  customElements.define('gift-with-purchase-pdp', GiftWithPurchasePDP)
}

class PushCartGWP {
  constructor () {
    /**
     * @property {string|null}
     * @public
     */
    this.activeModalIndex = null
    this.activeModalElement = null
    this.activeTriggerElement = null

    this.setupListeners()
    this.updateSelectionCounts()
  }

  /**
   * @param {string} index
   *
   * @returns {HTMLElement|null}
   */
  getModal (index) {
    return document.querySelector(`[data-push-cart-gwp-modal="${index}"]`)
  }

  /**
   * @param {string} index
   * @returns {HTMLElement|null}
   */
  getTrigger (index) {
    return document.querySelector(`[data-push-cart-gwp-trigger="${index}"]`)
  }

  /**
   * @returns {NodeListOf<HTMLElement>}
   */
  get triggers () {
    return document.querySelectorAll('[data-push-cart-gwp-trigger]')
  }

  /**
   * @returns {HTMLElement[]}
   */
  get modals () {
    return Array.from(document.querySelectorAll('[data-modal="gift-with-purchase"]'))
  }

  /**
   * @param {HTMLElement} trigger
   * @param {string|null} index
   * @param {string|null} campaignId
   *
   * @returns {HTMLElement|null}
   */
  getModalForTrigger (trigger, index, campaignId) {
    if (index) {
      const modalByIndex = this.getModal(index)
      if (modalByIndex) return modalByIndex
    }

    if (campaignId) {
      const modalByCampaign = document.querySelector(
        `[data-modal="gift-with-purchase"][data-gwp-campaign-id="${campaignId}"]`,
      )
      if (modalByCampaign) return modalByCampaign
    }

    const triggerPosition = Array.from(this.triggers).indexOf(trigger)
    const modalByPosition = this.modals[triggerPosition]
    if (modalByPosition) return modalByPosition

    return this.modals[0] || null
  }

  setupListeners () {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-push-cart-gwp-trigger]')
      if (trigger && !trigger.disabled) {
        const index = trigger.getAttribute('data-push-cart-gwp-trigger')
        this.openModal(index)
        return
      }

      const closeBtn = e.target.closest('[data-push-cart-gwp-modal-close]')
      if (closeBtn) {
        this.closeModal()
        return
      }

      if (this.activeModalIndex != null) {
        const activeModal = this.getModal(this.activeModalIndex)
        if (activeModal && e.target === activeModal) {
          this.closeModal()
        }
      }
    })

    document.addEventListener('keydown', (e) => {
      const trigger = e.target.closest('[data-push-cart-gwp-trigger]')
      if (trigger && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        const index = trigger.getAttribute('data-push-cart-gwp-trigger')
        this.openModal(index)
        return
      }

      if (e.key === 'Escape' && this.activeModalIndex != null) {
        this.closeModal()
      }
    })

    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-gwp-form]')
      if (form) {
        this.handleFormSubmit(e, form)
      }
    })

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_RENDER,
      () => this.updateSelectionCounts(),
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.GIFT_WITH_PURCHASE.OPEN_MODAL,
      (event) => {
        const campaignId = event.detail?.campaignId
        if (!campaignId) return

        const trigger = Array.from(this.triggers).find((item) => {
          return item.getAttribute('data-gwp-campaign-id') === campaignId
        })

        if (!trigger) return

        if (trigger.disabled) return

        const index = trigger.getAttribute('data-push-cart-gwp-trigger')
        const modal = this.getModalForTrigger(trigger, index, campaignId)
        if (!modal) return

        this.openModalElement(modal, trigger)
      },
    )
  }

  /**
   * @param {SubmitEvent} e
   * @param {HTMLFormElement} form
   */
  async handleFormSubmit (e, form) {
    e.preventDefault()

    const submitButton = form.querySelector('button[type="submit"]')

    if (submitButton.disabled) {
      return
    }

    submitButton.disabled = true
    submitButton.textContent = window.theme.strings.adding

    try {
      await window.BAO.Cart.addItemFromForm(form)

      this.closeModal()

      setTimeout(() => {
        submitButton.disabled = false
        submitButton.textContent = window.theme.strings.add
      }, 1000)
    } catch (err) {
      console.error('GWP: Failed to add item', err)

      submitButton.disabled = false
      submitButton.textContent = window.theme.strings.add
    }
  }

  /**
   * @param {string} index
   */
  openModal (index) {
    const modal = this.getModal(index)
    if (!modal) return

    const trigger = this.getTrigger(index)
    this.openModalElement(modal, trigger)
  }

  /**
   * @param {HTMLElement} modal
   * @param {HTMLElement|null} trigger
   */
  openModalElement (modal, trigger = null) {
    if (!modal) return

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal)
    }

    this.activeModalIndex = modal.getAttribute('data-push-cart-gwp-modal')
    this.activeModalElement = modal
    this.activeTriggerElement = trigger

    modal.style.setProperty('display', 'flex', 'important')
    modal.style.setProperty('visibility', 'visible', 'important')
    modal.style.setProperty('opacity', '1', 'important')
    modal.classList.add('mod-Modal-active')
    modal.setAttribute('aria-hidden', 'false')
    document.body.style.overflow = 'hidden'

    const firstInput = modal.querySelector(
      'button, input, select, textarea',
    )
    if (firstInput) {
      firstInput.focus()
    }
  }

  closeModal () {
    if (this.activeModalIndex == null && !this.activeModalElement) return

    const modal = this.activeModalElement || this.getModal(this.activeModalIndex)
    const trigger = this.activeTriggerElement || this.getTrigger(this.activeModalIndex)

    if (modal) {
      modal.classList.remove('mod-Modal-active')
      modal.setAttribute('aria-hidden', 'true')
      modal.style.removeProperty('display')
      modal.style.removeProperty('visibility')
      modal.style.removeProperty('opacity')
    }

    document.body.style.overflow = ''
    this.activeModalIndex = null
    this.activeModalElement = null
    this.activeTriggerElement = null

    if (trigger) {
      trigger.focus()
    }
  }

  updateSelectionCounts () {
    if (!window.BAO.Cart) return

    const triggers = this.triggers
    if (triggers.length === 0) return

    window.BAO.Cart.getCart().then((cart) => {
      triggers.forEach((trigger) => {
        const index = trigger.getAttribute('data-push-cart-gwp-trigger')
        const campaignId = trigger.getAttribute('data-gwp-campaign-id')
        const modal = this.getModalForTrigger(trigger, index, campaignId)

        const gwpCount = cart.items.filter((item) => {
          return (
            item.properties &&
            item.properties._gwp === 'true' &&
            item.properties._gwp_campaign === campaignId
          )
        }).length

        const countElement = trigger.querySelector(
          '.psh-GiftWithPurchase_Selected',
        )
        const titleElement = trigger.querySelector(
          '.psh-GiftWithPurchase_Title',
        )
        const maxCount = Number(trigger.getAttribute('data-gwp-max-count')) || 1
        const selectedLabel = trigger.getAttribute('data-gwp-selected-label') || `${maxCount} selected`
        const defaultTitle = trigger.getAttribute('data-gwp-title-default') || 'Select Complimentary Gift'
        const selectedTitle = trigger.getAttribute('data-gwp-title-selected') || `${maxCount} complimentary free gift added`

        if (countElement) {
          countElement.textContent = `${gwpCount}/${selectedLabel}`
        }

        if (titleElement) {
          titleElement.textContent = gwpCount >= maxCount
            ? selectedTitle
            : defaultTitle
        }

        const submitButton = modal?.querySelector(
          '[data-gwp-form] button[type="submit"]',
        )

        if (gwpCount >= maxCount) {
          trigger.disabled = true
          trigger.setAttribute('aria-disabled', 'true')
          trigger.classList.add('psh-GiftWithPurchase-disabled')

          if (submitButton) {
            submitButton.disabled = true
            submitButton.setAttribute('aria-disabled', 'true')
          }
        } else {
          trigger.disabled = false
          trigger.removeAttribute('aria-disabled')
          trigger.classList.remove('psh-GiftWithPurchase-disabled')

          if (submitButton) {
            submitButton.disabled = false
            submitButton.removeAttribute('aria-disabled')
          }
        }
      })
    })
  }
}

window.BAO = window.BAO || {}
window.BAO.PushCartGWP = new PushCartGWP()
